import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Paper,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Avatar
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  ViewWeek,
  ViewDay,
  CalendarMonth,
  ViewAgenda,
  WorkOutline,
  FilterListOff,
  Person as PersonIcon
} from '@mui/icons-material';
import { format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addWeeks, 
  addMonths, 
  isSameMonth, 
  isSameDay, 
  isToday,
  startOfDay,
  endOfDay,
  startOfWeek as startOfWeekFn,
  endOfWeek as endOfWeekFn,
  getHours,
  setHours,
  setMinutes
} from 'date-fns';
import { pl } from 'date-fns/locale';
import { 
  getStatusColor, 
  getStatusLabel,
  getTaskTypeColor, 
  getTaskTypeLabel, 
  getPriorityColor,
  getPriorityLabel,
  TASK_STATUS_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_TYPE
} from '../../models/TaskDTO';
import { useUserData, getUserFullName } from '../../hooks/useUserData';
import { applyMonthTransformations, getDefaultLanguageCode } from '../../utils/dateLocalization';

// Helper function to format dates with proper month names for the current language
const formatWithNominativeMonth = (date: Date, formatString: string, options?: { locale?: any }): string => {
  const formatted = format(date, formatString, options);

  // Get the language code from the locale or use default
  const languageCode = options?.locale?.code || getDefaultLanguageCode();

  // Apply language-specific month transformations
  return applyMonthTransformations(formatted, languageCode);
};

export type CalendarView = 'month' | 'week' | 'workWeek' | 'day' | 'agenda';

export interface CalendarEvent {
  Id: number;
  Subject: string;
  Description: string;
  StartTime: Date;
  EndTime: Date;
  Status: string;
  Priority: 'A' | 'B' | 'C' | 'D' | 'E';
  TaskType?: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER';
  assignedToUserId?: string;
  assignedToLead?: number;
  prefixId?: string;
}

interface CustomCalendarProps {
  events: CalendarEvent[];
  currentView: CalendarView;
  selectedDate: Date;
  onViewChange: (view: CalendarView) => void;
  onDateChange: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
  onCellClick: (date: Date) => void;
  onEventUpdate?: (eventId: number, updates: { startTime: Date; endTime: Date }) => Promise<void>;
  loading?: boolean;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  events,
  currentView,
  selectedDate,
  onViewChange,
  onDateChange,
  onEventClick,
  onCellClick,
  onEventUpdate,
  loading = false
}) => {
  // Hour range configuration - can be easily replaced with backend data in the future
  const startTimeRange = 0; // 0:00 AM
  const endTimeRange = 24; // 24:00 PM (20:00)

  const [currentDate, setCurrentDate] = useState(selectedDate);

  // Sync currentDate with selectedDate prop
  useEffect(() => {
    setCurrentDate(selectedDate);
  }, [selectedDate]);

  // Fetch user data using optimized hook
  const { domainUsers } = useUserData();

  // Helper functions for user display
  const getUserFullNameById = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';
    const user = domainUsers.find(u => u.id === userId);
    return getUserFullName(user);
  };

  const getUserInitials = (userId?: string) => {
    if (!userId) return '';
    const user = domainUsers.find(u => u.id === userId);
    if (!user) return '';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };


  // Drag and resize state
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    isResizing: boolean;
    eventId: number | null;
    startX: number;
    startY: number;
    startTime: Date | null;
    endTime: Date | null;
    originalStartTime: Date | null;
    originalEndTime: Date | null;
    resizeHandle: 'top' | 'bottom' | null;
    hasMoved: boolean;
  }>({
    isDragging: false,
    isResizing: false,
    eventId: null,
    startX: 0,
    startY: 0,
    startTime: null,
    endTime: null,
    originalStartTime: null,
    originalEndTime: null,
    resizeHandle: null,
    hasMoved: false,
  });

  // Tooltip state for resize operations
  const [resizeTooltip, setResizeTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    time: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    time: '',
  });

  // Tooltip state for drag operations
  const [dragTooltip, setDragTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    date: string;
    timeRange: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    date: '',
    timeRange: '',
  });

  // Optimistic updates state - tracks events that have been moved/resized but not yet confirmed by backend
  // Extended to include day information for cross-day moves in week/month views
  const [optimisticUpdates, setOptimisticUpdates] = useState<Map<number, { startTime: Date; endTime: Date }>>(new Map());

  // Clear optimistic updates when actual event data is updated from cache refresh
  useEffect(() => {
    if (optimisticUpdates.size > 0) {
      const updatesToClear: number[] = [];
      
      optimisticUpdates.forEach((optimisticData, eventId) => {
        const actualEvent = events.find(e => e.Id === eventId);
        if (actualEvent) {
          // Check if the actual event data matches the optimistic update
          const actualStartTime = new Date(actualEvent.StartTime);
          const actualEndTime = new Date(actualEvent.EndTime);
          
          // If the times match (within 1 minute tolerance), clear the optimistic update
          const startTimeDiff = Math.abs(actualStartTime.getTime() - optimisticData.startTime.getTime());
          const endTimeDiff = Math.abs(actualEndTime.getTime() - optimisticData.endTime.getTime());
          
          if (startTimeDiff < 60000 && endTimeDiff < 60000) { // 1 minute tolerance
            updatesToClear.push(eventId);
          }
        }
      });
      
      if (updatesToClear.length > 0) {
        setOptimisticUpdates(prev => {
          const newMap = new Map(prev);
          updatesToClear.forEach(eventId => newMap.delete(eventId));
          return newMap;
        });
      }
    }
  }, [events, optimisticUpdates]);

  const calendarRef = useRef<HTMLDivElement>(null);
  const weekViewRef = useRef<HTMLDivElement>(null);
  const dayViewRef = useRef<HTMLDivElement>(null);

  // Function to scroll to current time
  const scrollToCurrentTime = useCallback(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();

    if (currentView === 'day' && dayViewRef.current) {
      // Day view: 80px per hour
      const scrollPosition = (currentHour * 80) + (currentMinutes / 60 * 80) - 200; // Offset to center the current time
      dayViewRef.current.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    } else if ((currentView === 'week' || currentView === 'workWeek') && weekViewRef.current) {
      // Week view: 60px per hour
      const scrollPosition = (currentHour * 60) + (currentMinutes / 60 * 60) - 200; // Offset to center the current time
      weekViewRef.current.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [currentView]);

  // Effect to scroll to current time when view changes to time-based views
  useEffect(() => {
    if (currentView === 'day' || currentView === 'week' || currentView === 'workWeek') {
      // Small delay to ensure the view has rendered
      const timer = setTimeout(() => {
        scrollToCurrentTime();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentView, scrollToCurrentTime]);

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    let newDate: Date;
    switch (currentView) {
      case 'day':
        newDate = addDays(currentDate, -1);
        break;
      case 'week':
      case 'workWeek':
        newDate = addWeeks(currentDate, -1);
        break;
      case 'month':
      case 'agenda':
        newDate = addMonths(currentDate, -1);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
    onDateChange(newDate);
  }, [currentDate, currentView, onDateChange]);

  const goToNext = useCallback(() => {
    let newDate: Date;
    switch (currentView) {
      case 'day':
        newDate = addDays(currentDate, 1);
        break;
      case 'week':
      case 'workWeek':
        newDate = addWeeks(currentDate, 1);
        break;
      case 'month':
      case 'agenda':
        newDate = addMonths(currentDate, 1);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
    onDateChange(newDate);
  }, [currentDate, currentView, onDateChange]);

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    onDateChange(today);
    onViewChange('day');
  }, [onDateChange, onViewChange]);

  // Handler for view changes that should navigate to current time
  const handleViewChangeWithCurrentTime = useCallback((view: CalendarView) => {
    // For week, workWeek, and day views, navigate to current time
    if (view === 'week' || view === 'workWeek' || view === 'day') {
      const today = new Date();
      setCurrentDate(today);
      onDateChange(today);
    }
    onViewChange(view);
  }, [onDateChange, onViewChange]);

  // Drag and resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent, eventId: number, resizeHandle?: 'top' | 'bottom') => {
    e.preventDefault();
    e.stopPropagation();

    const event = events.find(ev => ev.Id === eventId);
    if (!event || !onEventUpdate) return;

    const rect = calendarRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDragState({
      isDragging: false, // Don't start dragging immediately
      isResizing: !!resizeHandle, // Resizing starts immediately for resize handles
      eventId,
      startX: e.clientX,
      startY: e.clientY,
      startTime: new Date(event.StartTime),
      endTime: new Date(event.EndTime),
      originalStartTime: new Date(event.StartTime),
      originalEndTime: new Date(event.EndTime),
      resizeHandle: resizeHandle || null,
      hasMoved: false,
    });
  }, [events, onEventUpdate]);

  // Helper function to round time to 15-minute intervals for tooltip display
  const roundTo15Minutes = useCallback((date: Date) => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    const newDate = new Date(date);
    newDate.setMinutes(roundedMinutes, 0, 0);
    return newDate;
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Check if we're tracking mouse movement (either dragging, resizing, or preparing to drag)
    if (!dragState.isDragging && !dragState.isResizing && !dragState.eventId) return;
    if (!dragState.originalStartTime || !dragState.originalEndTime) return;

    const initialDeltaX = e.clientX - dragState.startX;
    const initialDeltaY = e.clientY - dragState.startY;
    const distance = Math.sqrt(initialDeltaX * initialDeltaX + initialDeltaY * initialDeltaY);

    // If we're not yet dragging and not resizing, check if we should start dragging
    if (!dragState.isDragging && !dragState.isResizing && distance > 5) {
      setDragState(prev => ({
        ...prev,
        isDragging: true,
        hasMoved: true,
      }));
      return;
    }

    // If we haven't moved enough and we're not resizing, don't do anything
    if (!dragState.isDragging && !dragState.isResizing) return;

    const deltaY = e.clientY - dragState.startY;
    const minutesPerPixel = currentView === 'day' ? 60 / 80 : 60 / 60; // 80px per hour in day view, 60px in week view
    const deltaMinutes = Math.round(deltaY * minutesPerPixel);

    if (dragState.isDragging) {
      // Move the entire event
      let newStartTime = new Date(dragState.originalStartTime);
      let newEndTime = new Date(dragState.originalEndTime);

      if (currentView === 'month') {
        // In month view, only change the date, preserve the time
        if (calendarRef.current) {
          const rect = calendarRef.current.getBoundingClientRect();

          // Find the actual calendar grid by looking for the first calendar row
          const calendarGrid = calendarRef.current.querySelector('[data-calendar-grid]') || 
                              calendarRef.current.querySelector('div > div:last-child');

          let calendarTop = rect.top;
          let calendarHeight = rect.height;

          if (calendarGrid) {
            const gridRect = calendarGrid.getBoundingClientRect();
            calendarTop = gridRect.top;
            calendarHeight = gridRect.height;
          } else {
            // Fallback: estimate header height more accurately
            // Header has p: 2 (16px) + content height (~48px) + border (1px) = ~65px
            calendarTop = rect.top + 65;
            calendarHeight = rect.height - 65;
          }

          const calendarLeft = rect.left;
          const calendarWidth = rect.width;

          // Calculate which day cell the mouse is over
          const mouseX = e.clientX - calendarLeft;
          const mouseY = e.clientY - calendarTop;

          // Calculate the actual number of rows in the calendar
          const monthStart = startOfMonth(currentDate);
          const monthEnd = endOfMonth(currentDate);
          const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
          const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
          const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;
          const actualRows = Math.ceil(totalDays / 7);

          // Use actual row count for more precise calculation
          const rowHeight = calendarHeight / actualRows;
          const dayWidth = calendarWidth / 7;

          // Simple approach with fixed offsets - separate control for vertical and horizontal
          const VERTICAL_OFFSET = -50; // Fixed offset in pixels for up/down movement
          const HORIZONTAL_OFFSET = 0; // Fixed offset in pixels for left/right movement

          // Calculate which cell the mouse is currently in with separate offsets
          const offsetMouseY = mouseY + VERTICAL_OFFSET;
          const offsetMouseX = mouseX + HORIZONTAL_OFFSET;

          // Clamp to calendar bounds
          const clampedMouseY = Math.max(0, Math.min(offsetMouseY, calendarHeight - 1));
          const clampedMouseX = Math.max(0, Math.min(offsetMouseX, calendarWidth - 1));

          const targetRowIndex = Math.floor(clampedMouseY / rowHeight);
          const targetDayIndex = Math.floor(clampedMouseX / dayWidth);

          // Ensure indices are within valid bounds
          const finalRowIndex = Math.max(0, Math.min(targetRowIndex, actualRows - 1));
          const finalDayIndex = Math.max(0, Math.min(targetDayIndex, 6));

          // Calculate the target date using the final indices
          const targetDate = addDays(startDate, finalRowIndex * 7 + finalDayIndex);

          // Get original time components
          const originalHours = dragState.originalStartTime.getHours();
          const originalMinutes = dragState.originalStartTime.getMinutes();
          const originalSeconds = dragState.originalStartTime.getSeconds();

          // Set new date with preserved time
          newStartTime = new Date(targetDate);
          newStartTime.setHours(originalHours, originalMinutes, originalSeconds);

          // Calculate duration and set end time
          const originalDuration = dragState.originalEndTime.getTime() - dragState.originalStartTime.getTime();
          newEndTime = new Date(newStartTime.getTime() + originalDuration);
        }
      } else {
        // For week/day views, apply time delta (vertical movement)
        newStartTime.setMinutes(newStartTime.getMinutes() + deltaMinutes);
        newEndTime.setMinutes(newEndTime.getMinutes() + deltaMinutes);

        // Handle horizontal movement (day changes) in week view
        if ((currentView === 'week' || currentView === 'workWeek') && calendarRef.current) {
          const rect = calendarRef.current.getBoundingClientRect();
          const calendarLeft = rect.left + 60; // Account for time column (60px)
          const calendarWidth = rect.width - 60;
          const days = currentView === 'workWeek' ? 5 : 7;
          const dayWidth = calendarWidth / days;

          // Calculate which day column the mouse is over
          const mouseX = e.clientX - calendarLeft;
          const dayIndex = Math.floor(mouseX / dayWidth);
          const clampedDayIndex = Math.max(0, Math.min(dayIndex, days - 1));

          // Calculate the original day index
          const weekStart = startOfWeekFn(currentDate, { weekStartsOn: 1 });
          const originalDay = new Date(dragState.originalStartTime);
          const originalDayIndex = Math.floor((originalDay.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000));

          // Calculate day difference
          const dayDifference = clampedDayIndex - originalDayIndex;

          if (dayDifference !== 0) {
            // Apply day difference to both start and end times
            newStartTime = addDays(newStartTime, dayDifference);
            newEndTime = addDays(newEndTime, dayDifference);
          }
        }

        // Snap to 30-minute intervals during drag (only for week/day views)
        const snapTo30Minutes = (date: Date) => {
          const minutes = date.getMinutes();
          const roundedMinutes = Math.round(minutes / 30) * 30;
          const newDate = new Date(date);
          newDate.setMinutes(roundedMinutes, 0, 0);
          return newDate;
        };

        newStartTime = snapTo30Minutes(newStartTime);
        const originalDuration = dragState.originalEndTime.getTime() - dragState.originalStartTime.getTime();
        newEndTime = new Date(newStartTime.getTime() + originalDuration);
      }

      setDragState(prev => ({
        ...prev,
        startTime: newStartTime,
        endTime: newEndTime,
      }));

      // Show drag tooltip with date and time range
      const dateStr = format(newStartTime, 'EEEE, d MMMM yyyy', { locale: pl });
      const draggedEvent = events.find(ev => ev.Id === dragState.eventId);
      const timeRangeStr = draggedEvent?.TaskType === 'MEETING' 
        ? `${format(newStartTime, 'HH:mm', { locale: pl })} - ${format(newEndTime, 'HH:mm', { locale: pl })}`
        : format(newStartTime, 'HH:mm', { locale: pl });
      setDragTooltip({
        visible: true,
        x: e.clientX + 10,
        y: e.clientY - 50,
        date: dateStr,
        timeRange: timeRangeStr,
      });
    } else if (dragState.isResizing) {
      // Resize the event with real-time visual feedback
      const newStartTime = new Date(dragState.originalStartTime);
      const newEndTime = new Date(dragState.originalEndTime);

      if (dragState.resizeHandle === 'top') {
        newStartTime.setMinutes(newStartTime.getMinutes() + deltaMinutes);
        // Ensure minimum duration of 30 minutes
        if (newStartTime.getTime() >= newEndTime.getTime() - 30 * 60 * 1000) {
          newStartTime.setTime(newEndTime.getTime() - 30 * 60 * 1000);
        }
        setDragState(prev => ({
          ...prev,
          startTime: newStartTime,
          endTime: newEndTime,
        }));

        // Show tooltip with target start time (rounded to 15-minute intervals)
        const roundedStartTime = roundTo15Minutes(newStartTime);
        setResizeTooltip({
          visible: true,
          x: e.clientX + 10,
          y: e.clientY - 30,
          time: format(roundedStartTime, 'HH:mm', { locale: pl }),
        });
      } else if (dragState.resizeHandle === 'bottom') {
        newEndTime.setMinutes(newEndTime.getMinutes() + deltaMinutes);
        // Ensure minimum duration of 30 minutes
        if (newEndTime.getTime() <= newStartTime.getTime() + 30 * 60 * 1000) {
          newEndTime.setTime(newStartTime.getTime() + 30 * 60 * 1000);
        }
        setDragState(prev => ({
          ...prev,
          startTime: newStartTime,
          endTime: newEndTime,
        }));

        // Show tooltip with target end time (rounded to 15-minute intervals)
        const roundedEndTime = roundTo15Minutes(newEndTime);
        setResizeTooltip({
          visible: true,
          x: e.clientX + 10,
          y: e.clientY - 30,
          time: format(roundedEndTime, 'HH:mm', { locale: pl }),
        });
      }
    }
  }, [dragState, currentView, currentDate, roundTo15Minutes]);

  const handleMouseUp = useCallback(async () => {
    // If we have an eventId but haven't moved significantly, treat it as a click
    if (dragState.eventId && !dragState.hasMoved && !dragState.isDragging && !dragState.isResizing) {
      const event = events.find(ev => ev.Id === dragState.eventId);
      if (event) {
        // Reset drag state first
        setDragState({
          isDragging: false,
          isResizing: false,
          eventId: null,
          startX: 0,
          startY: 0,
          startTime: null,
          endTime: null,
          originalStartTime: null,
          originalEndTime: null,
          resizeHandle: null,
          hasMoved: false,
        });
        // Hide tooltips
        setResizeTooltip({
          visible: false,
          x: 0,
          y: 0,
          time: '',
        });
        setDragTooltip({
          visible: false,
          x: 0,
          y: 0,
          date: '',
          timeRange: '',
        });
        // Then trigger the click event
        onEventClick(event);
        return;
      }
    }

    if (!dragState.isDragging && !dragState.isResizing) return;
    if (!dragState.eventId || !dragState.originalStartTime || !dragState.originalEndTime || !onEventUpdate) return;

    // Calculate new times for the update
    let newStartTime = dragState.startTime || dragState.originalStartTime;
    let newEndTime: Date;

    if (dragState.isDragging) {
      // Calculate new end time based on original duration
      const originalDuration = dragState.originalEndTime.getTime() - dragState.originalStartTime.getTime();
      newEndTime = new Date(newStartTime.getTime() + originalDuration);
    } else {
      // For resizing, calculate based on which handle was used
      if (dragState.resizeHandle === 'top') {
        newEndTime = new Date(dragState.originalEndTime);
      } else {
        newStartTime = new Date(dragState.originalStartTime);
        newEndTime = dragState.endTime || dragState.originalEndTime;
      }
    }

    // Round to nearest 30 minutes for dragging, 15 minutes for resizing
    const roundToInterval = (date: Date, intervalMinutes: number) => {
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / intervalMinutes) * intervalMinutes;
      const newDate = new Date(date);
      newDate.setMinutes(roundedMinutes, 0, 0);
      return newDate;
    };

    if (dragState.isDragging) {
      // Use 30-minute intervals for dragging
      newStartTime = roundToInterval(newStartTime, 30);
      newEndTime = roundToInterval(newEndTime, 30);
    } else {
      // Use 15-minute intervals for resizing
      newStartTime = roundToInterval(newStartTime, 15);
      newEndTime = roundToInterval(newEndTime, 15);
    }

    // Ensure end time is after start time
    const minDuration = dragState.isDragging ? 30 * 60 * 1000 : 15 * 60 * 1000;
    if (newEndTime.getTime() <= newStartTime.getTime()) {
      newEndTime = new Date(newStartTime.getTime() + minDuration);
    }

    // Store the event ID and new times for the background update
    const eventIdToUpdate = dragState.eventId;
    const updateData = {
      startTime: newStartTime,
      endTime: newEndTime,
    };

    // Store optimistic update to prevent visual flicker
    if (eventIdToUpdate) {
      setOptimisticUpdates(prev => new Map(prev.set(eventIdToUpdate, updateData)));
    }

    // Reset drag state immediately to prevent UI blocking
    setDragState({
      isDragging: false,
      isResizing: false,
      eventId: null,
      startX: 0,
      startY: 0,
      startTime: null,
      endTime: null,
      originalStartTime: null,
      originalEndTime: null,
      resizeHandle: null,
      hasMoved: false,
    });

    // Hide tooltips immediately
    setResizeTooltip({
      visible: false,
      x: 0,
      y: 0,
      time: '',
    });
    setDragTooltip({
      visible: false,
      x: 0,
      y: 0,
      date: '',
      timeRange: '',
    });

    // Perform the backend update in the background without blocking UI
    if (onEventUpdate && eventIdToUpdate) {
      onEventUpdate(eventIdToUpdate, updateData)
        .then(() => {
          // Don't clear optimistic update immediately - let it persist until cache refresh
          // The optimistic update will be cleared when new data arrives and component re-renders
          console.log('Backend update successful, keeping optimistic update until cache refresh');
        })
        .catch(error => {
          console.error('Error updating event in background:', error);
          // Clear optimistic update on error and revert to original position
          setOptimisticUpdates(prev => {
            const newMap = new Map(prev);
            newMap.delete(eventIdToUpdate);
            return newMap;
          });
          // Optionally, you could show a toast notification here
          // or implement retry logic
        });
    }
  }, [dragState, onEventUpdate, events, onEventClick]);

  // Add global mouse event listeners
  React.useEffect(() => {
    if (dragState.isDragging || dragState.isResizing || dragState.eventId) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      if (dragState.isDragging || dragState.isResizing) {
        document.body.style.cursor = dragState.isDragging ? 'grabbing' : 'ns-resize';
        document.body.style.userSelect = 'none';
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [dragState.isDragging, dragState.isResizing, dragState.eventId, handleMouseMove, handleMouseUp]);

  // Get title based on current view and date
  const getTitle = useCallback(() => {
    switch (currentView) {
      case 'day':
        return formatWithNominativeMonth(currentDate, 'EEEE, d MMMM yyyy', { locale: pl });
      case 'week':
      case 'workWeek':
        const weekStart = startOfWeekFn(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeekFn(currentDate, { weekStartsOn: 1 });
        return `${format(weekStart, 'd')} - ${format(weekEnd, 'd MMMM yyyy', { locale: pl })}`;
      case 'month':
        return formatWithNominativeMonth(currentDate, 'MMMM yyyy', { locale: pl });
      case 'agenda':
        return formatWithNominativeMonth(currentDate, 'MMMM yyyy', { locale: pl });
      default:
        return '';
    }
  }, [currentDate, currentView]);

  // Filter events for current view
  const filteredEvents = useMemo(() => {
    let startDate: Date;
    let endDate: Date;

    switch (currentView) {
      case 'day':
        startDate = startOfDay(currentDate);
        endDate = endOfDay(currentDate);
        break;
      case 'week':
        startDate = startOfWeekFn(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeekFn(currentDate, { weekStartsOn: 1 });
        break;
      case 'workWeek':
        startDate = startOfWeekFn(currentDate, { weekStartsOn: 1 });
        endDate = endOfDay(addDays(startDate, 4)); // Monday to Friday (end of Friday)
        break;
      case 'month':
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
        endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
        break;
      case 'agenda':
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
        break;
      default:
        return events;
    }

    return events.filter(event => {
      const eventStart = new Date(event.StartTime);
      return eventStart >= startDate && eventStart <= endDate;
    });
  }, [events, currentDate, currentView]);


  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;

    // Week header
    const weekDays = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const currentDay = new Date(day);
        const dayEvents = filteredEvents.filter(event => {
          // Check if this event is currently being dragged
          const isBeingDragged = dragState.isDragging && dragState.eventId === event.Id;
          // Check if this event has an optimistic update (moved but not yet confirmed)
          const hasOptimisticUpdate = optimisticUpdates.has(event.Id);

          if (isBeingDragged && dragState.startTime) {
            // If event is being dragged, only show it in the target day cell
            return isSameDay(dragState.startTime, currentDay);
          } else if (hasOptimisticUpdate) {
            // If event has optimistic update, show it in the optimistic day cell
            const optimisticUpdate = optimisticUpdates.get(event.Id)!;
            return isSameDay(optimisticUpdate.startTime, currentDay);
          } else {
            // If event is not being dragged and has no optimistic update, show it in its original day
            return isSameDay(new Date(event.StartTime), currentDay);
          }
        });

        days.push(
          <Box
            key={currentDay.toString()}
            sx={{
              width: 'calc(100% / 7)',
              minHeight: 120,
              border: '1px solid #e0e0e0',
              p: 1,
              cursor: 'pointer',
              backgroundColor: isSameMonth(currentDay, monthStart) ? 'white' : '#f5f5f5',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
            onClick={() => onCellClick(currentDay)}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: isToday(currentDay) ? 'bold' : 'normal',
                color: isToday(currentDay) ? 'primary.main' : isSameMonth(currentDay, monthStart) ? 'text.primary' : 'text.secondary'
              }}
            >
              {format(currentDay, 'd')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {dayEvents.slice(0, 3).map((event, index) => {
                // Use optimistic times if available, drag state times if being dragged, otherwise original times
                const isDraggedEvent = dragState.isDragging && dragState.eventId === event.Id;
                const hasOptimisticUpdate = optimisticUpdates.has(event.Id);
                
                let startDate: Date, endDate: Date;
                if (isDraggedEvent && dragState.startTime && dragState.endTime) {
                  startDate = dragState.startTime;
                  endDate = dragState.endTime;
                } else if (hasOptimisticUpdate) {
                  const optimisticUpdate = optimisticUpdates.get(event.Id)!;
                  startDate = optimisticUpdate.startTime;
                  endDate = optimisticUpdate.endTime;
                } else {
                  startDate = new Date(event.StartTime);
                  endDate = new Date(event.EndTime);
                }
                
                const startTime = format(startDate, 'HH:mm');
                const endTime = format(endDate, 'HH:mm');
                const taskTypeLabel = getTaskTypeLabel(event.TaskType);
                const eventLabel = `${startTime}-${endTime} ${taskTypeLabel}`;

                return (
                  <Tooltip
                    key={event.Id}
                    title={
                      <Box sx={{ maxWidth: 300 }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '0.75rem', 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          mb: 0.5 
                        }}>
                          {event.Subject}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                          <strong>ID:</strong> {event.prefixId && event.Id ? `${event.prefixId}-${event.Id}` : event.Id}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                          <strong>Typ:</strong> {taskTypeLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                          <strong>Czas:</strong> {startTime} - {endTime}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                          <strong>Status:</strong> {getStatusLabel(event.Status)}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                          <strong>Priorytet:</strong> {getPriorityLabel(event.Priority)}
                        </Typography>
                        {event.Description && (
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.65rem', 
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: 0.5 
                          }}>
                            <strong>Opis:</strong> {event.Description}
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Chip
                      label={eventLabel}
                      size="small"
                      sx={{
                        mb: 0.3,
                        mr: 0.3,
                        fontSize: '0.55rem',
                        height: 18,
                        width: 'calc(100% - 4px)',
                        backgroundColor: getTaskTypeColor(event.TaskType),
                        color: 'white',
                        cursor: dragState.isDragging && dragState.eventId === event.Id ? 'grabbing' : 'grab',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.55rem',
                          padding: '0 4px'
                        },
                        '&:hover': {
                          opacity: 0.8
                        },
                        opacity: dragState.isDragging && dragState.eventId === event.Id ? 0.7 : 1,
                        transform: dragState.isDragging && dragState.eventId === event.Id ? 'scale(1.05)' : 'scale(1)',
                        transition: 'transform 0.1s ease-in-out'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      onMouseDown={(e) => {
                        if (onEventUpdate) {
                          handleMouseDown(e, event.Id);
                        }
                      }}
                    />
                  </Tooltip>
                );
              })}
              {dayEvents.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  +{dayEvents.length - 3} więcej
                </Typography>
              )}
            </Box>
          </Box>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <Box key={day.toString()} sx={{ display: 'flex' }}>
          {days}
        </Box>
      );
      days = [];
    }

    return (
        <Box>
          {/* Week header */}
          <Box sx={{ display: 'flex', mb: 1 }}>
            {weekDays.map(dayName => (
                <Box
                    key={dayName}
                    sx={{
                      width: 'calc(100% / 7)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#f5f5f5',
                      p: 0.5
                    }}
                >
                  <Typography
                      variant="caption"
                      sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    {dayName}
                  </Typography>
                </Box>
            ))}
          </Box>
          {/* Calendar grid */}
          <Box data-calendar-grid>
            {rows}
          </Box>
        </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeekFn(currentDate, { weekStartsOn: 1 });
    const days = currentView === 'workWeek' ? 5 : 7;
    const hours = Array.from({ length: endTimeRange - startTimeRange + 1 }, (_, i) => i + startTimeRange); // Hours from startTimeRange to endTimeRange
    const now = new Date();

    // Calculate current time position for today (adjusted for startTimeRange-endTimeRange range)
    const currentTimeOffset = ((now.getHours() - startTimeRange) * 60) + (now.getMinutes() / 60 * 60); // 60px per hour

    const getEventsForDay = (day: Date) => {
      const events = filteredEvents
          .filter(event => {
            // Check if this event is currently being dragged
            const isBeingDragged = dragState.isDragging && dragState.eventId === event.Id;
            // Check if this event has an optimistic update (moved but not yet confirmed)
            const hasOptimisticUpdate = optimisticUpdates.has(event.Id);

            if (isBeingDragged && dragState.startTime) {
              // If event is being dragged, only show it in the target day column
              return isSameDay(dragState.startTime, day);
            } else if (hasOptimisticUpdate) {
              // If event has optimistic update, show it in the optimistic day column
              const optimisticUpdate = optimisticUpdates.get(event.Id)!;
              return isSameDay(optimisticUpdate.startTime, day);
            } else {
              // If event is not being dragged and has no optimistic update, show it in its original day
              return isSameDay(new Date(event.StartTime), day);
            }
          })
          .map(event => {
            // Use drag state times if this event is being dragged, optimistic times if available, otherwise use original times
            const isDraggedEvent = dragState.isDragging && dragState.eventId === event.Id;
            const hasOptimisticUpdate = optimisticUpdates.has(event.Id);
            
            let start: Date, end: Date;
            if (isDraggedEvent && dragState.startTime && dragState.endTime) {
              // Use real-time drag state
              start = dragState.startTime;
              end = dragState.endTime;
            } else if (hasOptimisticUpdate) {
              // Use optimistic update times
              const optimisticUpdate = optimisticUpdates.get(event.Id)!;
              start = optimisticUpdate.startTime;
              end = optimisticUpdate.endTime;
            } else {
              // Use original times
              start = new Date(event.StartTime);
              end = new Date(event.EndTime);
            }

            const startMinutes = (start.getHours() - startTimeRange) * 60 + start.getMinutes();
            const endMinutes = (end.getHours() - startTimeRange) * 60 + end.getMinutes();
            const topOffset = (startMinutes / 60) * 60;
            const duration = Math.max((endMinutes - startMinutes) / 60, 0.5);
            return { ...event, topOffset, duration, startMinutes, endMinutes };
          });

      events.sort((a, b) => a.startMinutes - b.startMinutes);

      const groups: any[][] = [];
      let currentGroup: any[] = [];

      for (let i = 0; i < events.length; i++) {
        const current = events[i];

        if (currentGroup.length === 0) {
          currentGroup.push(current);
        } else {
          const overlaps = current.startMinutes < Math.max(...currentGroup.map(e => e.endMinutes));

          if (overlaps) {
            currentGroup.push(current);
          } else {
            groups.push([...currentGroup]);
            currentGroup = [current];
          }
        }
      }

      if (currentGroup.length > 0) {
        groups.push([...currentGroup]);
      }

      const positioned: any[] = [];

      for (const group of groups) {
        // Kolumny
        const slots: any[][] = [];

        for (const event of group) {
          let placed = false;
          for (let i = 0; i < slots.length; i++) {
            const col = slots[i];
            if (col[col.length - 1].endMinutes <= event.startMinutes) {
              col.push(event);
              positioned.push({
                ...event,
                slotIndex: i,
                totalSlots: group.length
              });
              placed = true;
              break;
            }
          }

          if (!placed) {
            slots.push([event]);
            positioned.push({
              ...event,
              slotIndex: slots.length - 1,
              totalSlots: group.length
            });
          }
        }

        const maxSlots = slots.length;
        for (const ev of positioned.filter(e => group.some(g => g.Id === e.Id))) {
          ev.totalSlots = maxSlots;
          ev.widthPercent = 100 / maxSlots;
          ev.leftPercent = (100 / maxSlots) * ev.slotIndex;
        }
      }

      return positioned;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `60px repeat(${days}, 1fr)`, borderBottom: '1px solid #e0e0e0' }}>
            <Box />
            {Array.from({ length: days }, (_, i) => {
              const day = addDays(weekStart, i);
              return (
                  <Box
                      key={i}
                      sx={{
                        textAlign: 'center',
                        p: 0.5,
                        backgroundColor: isToday(day) ? 'primary.light' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: isToday(day) ? 'primary.main' : 'rgba(25, 118, 210, 0.1)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '& .MuiTypography-root': {
                            color: isToday(day) ? 'white' : 'primary.main',
                            fontWeight: 'bold'
                          }
                        }
                      }}
                      onClick={() => {
                        // Navigate to day view for the selected day
                        onDateChange(day);
                        onViewChange('day');
                      }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{format(day, 'EEEE', { locale: pl })}</Typography>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.9rem' }}>{format(day, 'd')}</Typography>
                  </Box>
              );
            })}
          </Box>

          {/* Grid + Events */}
          <Box ref={weekViewRef} sx={{ position: 'relative', flex: 1, overflow: 'auto' }}>
            {/* Time Grid */}
            <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `60px repeat(${days}, 1fr)`,
                  gridAutoRows: '60px',
                  width: '100%',
                  borderLeft: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0'
                }}
            >
              {hours.map(hour =>
                  Array.from({ length: days + 1 }, (_, i) => (
                      <Box
                          key={`${hour}-${i}`}
                          sx={{
                            borderRight: '1px solid #e0e0e0',
                            borderTop: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: i === 0 ? 'center' : 'unset',
                            bgcolor: i === 0 ? '#fafafa' : 'transparent',
                            cursor: i === 0 ? 'default' : 'pointer',
                            px: 0.5,
                            position: 'relative',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': i === 0 ? {} : {
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              transform: 'scale(1.02)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              zIndex: 1,
                              '&::before': {
                                content: `"${hour.toString().padStart(2, '0')}:00 - ${format(addDays(weekStart, i - 1), 'EEEE d MMMM', { locale: pl })}"`,
                                position: 'absolute',
                                top: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                whiteSpace: 'nowrap',
                                zIndex: 10,
                                pointerEvents: 'none'
                              }
                            }
                          }}
                          onClick={() => {
                            if (i > 0) {
                              const day = addDays(weekStart, i - 1);
                              const cellDate = setHours(setMinutes(day, 0), hour);
                              onCellClick(cellDate);
                            }
                          }}
                      >
                        {i === 0 && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                              {hour.toString().padStart(2, '0')}:00
                            </Typography>
                        )}
                      </Box>
                  ))
              )}
            </Box>

            {/* Events */}
            <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 60,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none'
                }}
            >
              {Array.from({ length: days }, (_, i) => {
                const day = addDays(weekStart, i);
                const events = getEventsForDay(day);

                return (
                    <Box
                        key={i}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: `${(100 / days) * i}%`,
                          width: `${100 / days}%`,
                          pointerEvents: 'none'
                        }}
                    >
                      {events.map(event => {
                        const isDragging = dragState.eventId === event.Id && dragState.isDragging;
                        const isResizing = dragState.eventId === event.Id && dragState.isResizing;
                        const isActive = isDragging || isResizing;

                        // Calculate real-time position and size for active events or optimistic updates
                        let topOffset = event.topOffset;
                        let height = event.duration * 60;

                        if (isActive && dragState.startTime && dragState.endTime) {
                          // Use real-time drag/resize position
                          const startMinutes = (dragState.startTime.getHours() - startTimeRange) * 60 + dragState.startTime.getMinutes();
                          const endMinutes = (dragState.endTime.getHours() - startTimeRange) * 60 + dragState.endTime.getMinutes();
                          topOffset = (startMinutes / 60) * 60; // 60px per hour
                          const duration = (endMinutes - startMinutes) / 60; // Duration in hours
                          height = Math.max(duration * 60, 30); // Minimum 30px display
                        } else if (optimisticUpdates.has(event.Id)) {
                          // Use optimistic update position to prevent flicker
                          const optimisticUpdate = optimisticUpdates.get(event.Id)!;
                          const startMinutes = (optimisticUpdate.startTime.getHours() - startTimeRange) * 60 + optimisticUpdate.startTime.getMinutes();
                          const endMinutes = (optimisticUpdate.endTime.getHours() - startTimeRange) * 60 + optimisticUpdate.endTime.getMinutes();
                          topOffset = (startMinutes / 60) * 60; // 60px per hour
                          const duration = (endMinutes - startMinutes) / 60; // Duration in hours
                          height = Math.max(duration * 60, 30); // Minimum 30px display
                        }

                        return (
                          <Tooltip
                            key={event.Id}
                            title={
                              <Box sx={{ maxWidth: 300 }}>
                                <Typography variant="caption" sx={{ 
                                  fontWeight: 'bold', 
                                  fontSize: '0.75rem', 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  mb: 0.5 
                                }}>
                                  {event.Subject}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                  <strong>ID:</strong> {event.prefixId && event.Id ? `${event.prefixId}-${event.Id}` : event.Id}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                  <strong>Typ:</strong> {getTaskTypeLabel(event.TaskType)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                  <strong>Czas:</strong> {event.TaskType === 'MEETING' 
                                    ? `${format(new Date(event.StartTime), 'HH:mm')} - ${format(new Date(event.EndTime), 'HH:mm')}`
                                    : format(new Date(event.StartTime), 'HH:mm')
                                  }
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                  <strong>Status:</strong> {getStatusLabel(event.Status)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                  <strong>Priorytet:</strong> {getPriorityLabel(event.Priority)}
                                </Typography>
                                {event.Description && (
                                  <Typography variant="caption" sx={{ 
                                    fontSize: '0.65rem', 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    mt: 0.5 
                                  }}>
                                    <strong>Opis:</strong> {event.Description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <Box
                                sx={{
                                  position: 'absolute',
                                  top: `${topOffset}px`,
                                  height: `${height}px`,
                                  width: `calc(${event.widthPercent}% - 6px)`,
                                  left: `calc(${event.leftPercent}% + 3px)`,
                                  backgroundColor: getTaskTypeColor(event.TaskType),
                                  color: 'white',
                                  borderRadius: 1,
                                  px: 0.5,
                                  py: 0.25,
                                  pointerEvents: 'auto',
                                  cursor: isDragging ? 'grabbing' : 'grab',
                                  zIndex: isActive ? 10 : 1,
                                  overflow: 'hidden',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  gap: '1px',
                                  fontSize: '0.65rem',
                                  lineHeight: 1.0,
                                  opacity: isActive ? 0.8 : 1,
                                  transform: isActive ? 'scale(1.02)' : 'scale(1)',
                                  transition: isActive ? 'none' : 'all 0.2s ease',
                                  border: isActive ? '2px solid rgba(255,255,255,0.5)' : 'none',
                                  '&:hover': { 
                                    opacity: isActive ? 0.8 : 0.9,
                                    '& .resize-handle': {
                                      opacity: 1
                                    }
                                  }
                                }}
                                onMouseDown={(e) => {
                                  if (onEventUpdate) {
                                    handleMouseDown(e, event.Id);
                                  } else {
                                    e.stopPropagation();
                                    onEventClick(event);
                                  }
                                }}
                            >
                              {/* Top resize handle */}
                              {onEventUpdate && event.TaskType === 'MEETING' && (
                                <Box
                                  className="resize-handle"
                                  sx={{
                                    position: 'absolute',
                                    top: -4,
                                    left: 0,
                                    right: 0,
                                    height: 8,
                                    cursor: 'ns-resize',
                                    opacity: 0.1,
                                    transition: 'all 0.2s ease',
                                    backgroundColor: 'rgba(255,255,255,0.4)',
                                    borderRadius: '4px 4px 0 0',
                                    '&:hover': {
                                      opacity: 0.8,
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      transform: 'scaleY(1.2)'
                                    }
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, event.Id, 'top');
                                  }}
                                />
                              )}

                              <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{
                                    fontSize: '0.6rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    pointerEvents: 'none'
                                  }}
                              >
                                {getTaskTypeLabel(event.TaskType)}
                              </Typography>
                              <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.55rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    opacity: 0.9,
                                    pointerEvents: 'none'
                                  }}
                              >
                                {event.TaskType === 'MEETING' 
                                  ? `${format(new Date(event.StartTime), 'HH:mm')} - ${format(new Date(event.EndTime), 'HH:mm')}`
                                  : format(new Date(event.StartTime), 'HH:mm')
                                }
                              </Typography>

                              {/* Bottom resize handle */}
                              {onEventUpdate && event.TaskType === 'MEETING' && (
                                <Box
                                  className="resize-handle"
                                  sx={{
                                    position: 'absolute',
                                    bottom: -4,
                                    left: 0,
                                    right: 0,
                                    height: 8,
                                    cursor: 'ns-resize',
                                    opacity: 0.1,
                                    transition: 'all 0.2s ease',
                                    backgroundColor: 'rgba(255,255,255,0.4)',
                                    borderRadius: '0 0 4px 4px',
                                    '&:hover': {
                                      opacity: 0.8,
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      transform: 'scaleY(1.2)'
                                    }
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, event.Id, 'bottom');
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                );
              })}

              {/* Current time indicator line for week view - only show if current time is within startTimeRange-endTimeRange range */}
              {now.getHours() >= startTimeRange && now.getHours() < (endTimeRange + 1) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: `${currentTimeOffset}px`,
                    left: 0,
                    right: 0,
                    height: '2px',
                    backgroundColor: '#ff4444',
                    zIndex: 10,
                    pointerEvents: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '-4px',
                      top: '-4px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#ff4444',
                      borderRadius: '50%',
                      border: '2px solid white'
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
    );
  };

  // Render day view
  const renderDayView = () => {
    const hours = Array.from({ length: endTimeRange - startTimeRange + 1 }, (_, i) => i + startTimeRange); // Hours from startTimeRange to endTimeRange
    const now = new Date();

    // Calculate current time position for any day (adjusted for startTimeRange-endTimeRange range)
    const currentTimeOffset = ((now.getHours() - startTimeRange) * 80) + (now.getMinutes() / 60 * 80);

    // Calculate event positions for the day with overlap detection
    const dayEventsFiltered = filteredEvents.filter(event => {
      const eventStart = new Date(event.StartTime);
      return isSameDay(eventStart, currentDate);
    }).map(event => {
      // Use drag state times if this event is being dragged, otherwise use original times
      const isDraggedEvent = dragState.isDragging && dragState.eventId === event.Id;
      const eventStart = isDraggedEvent && dragState.startTime ? dragState.startTime : new Date(event.StartTime);
      const eventEnd = isDraggedEvent && dragState.endTime ? dragState.endTime : new Date(event.EndTime);
      
      const startHour = getHours(eventStart);
      const startMinutes = eventStart.getMinutes();
      const endHour = getHours(eventEnd);
      const endMinutes = eventEnd.getMinutes();

      // Calculate position and height (adjusted for startTimeRange-endTimeRange range)
      const topOffset = ((startHour - startTimeRange) * 80) + (startMinutes / 60 * 80); // 80px per hour
      const duration = ((endHour * 60 + endMinutes) - (startHour * 60 + startMinutes)) / 60; // Duration in hours
      const height = Math.max(duration * 80, 40); // Minimum 40px display, 80px per hour
      
      // Calculate start and end minutes for overlap detection
      const startMinutesTotal = (startHour - startTimeRange) * 60 + startMinutes;
      const endMinutesTotal = (endHour - startTimeRange) * 60 + endMinutes;

      return {
        ...event,
        topOffset,
        height,
        startMinutes: startMinutesTotal,
        endMinutes: endMinutesTotal
      };
    });

    // Apply overlap detection logic (similar to weekly view)
    dayEventsFiltered.sort((a, b) => a.startMinutes - b.startMinutes);

    const groups: any[][] = [];
    let currentGroup: any[] = [];

    for (let i = 0; i < dayEventsFiltered.length; i++) {
      const current = dayEventsFiltered[i];

      if (currentGroup.length === 0) {
        currentGroup.push(current);
      } else {
        const overlaps = current.startMinutes < Math.max(...currentGroup.map(e => e.endMinutes));

        if (overlaps) {
          currentGroup.push(current);
        } else {
          groups.push([...currentGroup]);
          currentGroup = [current];
        }
      }
    }

    if (currentGroup.length > 0) {
      groups.push([...currentGroup]);
    }

    const dayEvents: any[] = [];

    for (const group of groups) {
      // Create slots for overlapping events
      const slots: any[][] = [];

      for (const event of group) {
        let placed = false;
        for (let i = 0; i < slots.length; i++) {
          const col = slots[i];
          if (col[col.length - 1].endMinutes <= event.startMinutes) {
            col.push(event);
            dayEvents.push({
              ...event,
              slotIndex: i,
              totalSlots: group.length
            });
            placed = true;
            break;
          }
        }

        if (!placed) {
          slots.push([event]);
          dayEvents.push({
            ...event,
            slotIndex: slots.length - 1,
            totalSlots: group.length
          });
        }
      }

      const maxSlots = slots.length;
      for (const ev of dayEvents.filter(e => group.some(g => g.Id === e.Id))) {
        ev.totalSlots = maxSlots;
        ev.widthPercent = 100 / maxSlots;
        ev.leftPercent = (100 / maxSlots) * ev.slotIndex;
      }
    }

    return (
      <Box ref={dayViewRef} sx={{ height: '100%', overflow: 'auto', position: 'relative' }}>
        {/* Hour lines */}
        {hours.map(hour => {
          const cellDate = setHours(setMinutes(currentDate, 0), hour);

          return (
            <Box
              key={hour}
              sx={{
                display: 'flex',
                minHeight: 80,
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
              onClick={() => onCellClick(cellDate)}
            >
              <Box sx={{ width: 80, p: 1, borderRight: '1px solid #e0e0e0' }}>
                <Typography variant="body2">
                  {hour.toString().padStart(2, '0')}:00
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
            </Box>
          );
        })}

        {/* Current time indicator line - only show if current time is within startTimeRange-endTimeRange range */}
        {now.getHours() >= startTimeRange && now.getHours() < (endTimeRange + 1) && (
          <Box
            sx={{
              position: 'absolute',
              top: `${currentTimeOffset}px`,
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: '#ff4444',
              zIndex: 10,
              pointerEvents: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '78px',
                top: '-4px',
                width: '8px',
                height: '8px',
                backgroundColor: '#ff4444',
                borderRadius: '50%',
                border: '2px solid white'
              }
            }}
          />
        )}

        {/* Events overlay */}
        <Box sx={{ position: 'absolute', top: 0, left: 80, right: 0, height: '100%', pointerEvents: 'none' }}>
          {dayEvents.map((event) => {
            const isDragging = dragState.eventId === event.Id && dragState.isDragging;
            const isResizing = dragState.eventId === event.Id && dragState.isResizing;
            const isActive = isDragging || isResizing;

            // Calculate real-time position and size for active events or optimistic updates
            let topOffset = event.topOffset;
            let height = event.height;

            if (isActive && dragState.startTime && dragState.endTime) {
              // Use real-time drag/resize position
              const startMinutes = (dragState.startTime.getHours() - startTimeRange) * 60 + dragState.startTime.getMinutes();
              const endMinutes = (dragState.endTime.getHours() - startTimeRange) * 60 + dragState.endTime.getMinutes();
              topOffset = (startMinutes / 60) * 80; // 80px per hour in day view
              const duration = (endMinutes - startMinutes) / 60; // Duration in hours
              height = Math.max(duration * 80, 40); // Minimum 40px display, 80px per hour
            } else if (optimisticUpdates.has(event.Id)) {
              // Use optimistic update position to prevent flicker
              const optimisticUpdate = optimisticUpdates.get(event.Id)!;
              const startMinutes = (optimisticUpdate.startTime.getHours() - startTimeRange) * 60 + optimisticUpdate.startTime.getMinutes();
              const endMinutes = (optimisticUpdate.endTime.getHours() - startTimeRange) * 60 + optimisticUpdate.endTime.getMinutes();
              topOffset = (startMinutes / 60) * 80; // 80px per hour in day view
              const duration = (endMinutes - startMinutes) / 60; // Duration in hours
              height = Math.max(duration * 80, 40); // Minimum 40px display, 80px per hour
            }

            return (
              <Tooltip
                key={event.Id}
                title={
                  <Box sx={{ maxWidth: 300 }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.75rem', 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: 0.5 
                    }}>
                      {event.Subject}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                      <strong>ID:</strong> {event.prefixId && event.Id ? `${event.prefixId}-${event.Id}` : event.Id}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                      <strong>Typ:</strong> {getTaskTypeLabel(event.TaskType)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                      <strong>Czas:</strong> {event.TaskType === 'MEETING' 
                        ? `${format(new Date(event.StartTime), 'HH:mm')} - ${format(new Date(event.EndTime), 'HH:mm')}`
                        : format(new Date(event.StartTime), 'HH:mm')
                      }
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                      <strong>Status:</strong> {getStatusLabel(event.Status)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                      <strong>Priorytet:</strong> {getPriorityLabel(event.Priority)}
                    </Typography>
                    {event.Description && (
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.65rem', 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mt: 0.5 
                      }}>
                        <strong>Opis:</strong> {event.Description}
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                <Card
                  sx={{
                    position: 'absolute',
                    top: `${topOffset}px`,
                    height: `${height}px`,
                    width: event.widthPercent ? `calc(${event.widthPercent}% - 6px)` : 'calc(100% - 16px)',
                    left: event.leftPercent !== undefined ? `calc(${event.leftPercent}% + 3px)` : '8px',
                    backgroundColor: getTaskTypeColor(event.TaskType),
                    color: 'white',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    pointerEvents: 'auto',
                    zIndex: isActive ? 10 : 1,
                    opacity: isActive ? 0.8 : 1,
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    transition: isActive ? 'none' : 'all 0.2s ease',
                    border: isActive ? '2px solid rgba(255,255,255,0.5)' : 'none',
                    '&:hover': {
                      opacity: isActive ? 0.8 : 0.9,
                      '& .resize-handle': {
                        opacity: 1
                      }
                    }
                  }}
                  onMouseDown={(e) => {
                    if (onEventUpdate) {
                      handleMouseDown(e, event.Id);
                    } else {
                      e.stopPropagation();
                      onEventClick(event);
                    }
                  }}
                >
                  {/* Top resize handle */}
                  {onEventUpdate && event.TaskType === 'MEETING' && (
                    <Box
                      className="resize-handle"
                      sx={{
                        position: 'absolute',
                        top: -4,
                        left: 0,
                        right: 0,
                        height: 8,
                        cursor: 'ns-resize',
                        opacity: 0.1,
                        transition: 'all 0.2s ease',
                        backgroundColor: 'rgba(255,255,255,0.4)',
                        borderRadius: '4px 4px 0 0',
                        zIndex: 2,
                        '&:hover': {
                          opacity: 0.8,
                          backgroundColor: 'rgba(255,255,255,0.7)',
                          transform: 'scaleY(1.2)'
                        }
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleMouseDown(e, event.Id, 'top');
                      }}
                    />
                  )}

                  <CardContent sx={{ 
                    p: 1, 
                    '&:last-child': { pb: 1 }, 
                    height: '100%', 
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    position: 'relative'
                  }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.2, fontSize: '0.7rem', lineHeight: 1.1 }}>
                      {getTaskTypeLabel(event.TaskType)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.1 }}>
                      {event.TaskType === 'MEETING' 
                        ? `${format(new Date(event.StartTime), 'HH:mm')} - ${format(new Date(event.EndTime), 'HH:mm')}`
                        : format(new Date(event.StartTime), 'HH:mm')
                      }
                    </Typography>
                    {event.Description && event.height > 60 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.2, opacity: 0.9, fontSize: '0.6rem', lineHeight: 1.1 }}>
                        {event.Description.length > 50 ? `${event.Description.substring(0, 50)}...` : event.Description}
                      </Typography>
                    )}
                  </CardContent>

                  {/* Bottom resize handle */}
                  {onEventUpdate && event.TaskType === 'MEETING' && (
                    <Box
                      className="resize-handle"
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 8,
                        cursor: 'ns-resize',
                        opacity: 0.1,
                        transition: 'all 0.2s ease',
                        backgroundColor: 'rgba(255,255,255,0.4)',
                        borderRadius: '0 0 4px 4px',
                        zIndex: 2,
                        '&:hover': {
                          opacity: 0.8,
                          backgroundColor: 'rgba(255,255,255,0.7)',
                          transform: 'scaleY(1.2)'
                        }
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleMouseDown(e, event.Id, 'bottom');
                      }}
                    />
                  )}
                </Card>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render agenda view
  const renderAgendaView = () => {
    const sortedEvents = [...filteredEvents].sort((a, b) => 
      new Date(a.StartTime).getTime() - new Date(b.StartTime).getTime()
    );

    if (sortedEvents.length === 0) {
      return (
        <Box sx={{ p: 1 , textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Brak wydarzeń w tym miesiącu
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1 }}>
        {sortedEvents.map(event => (
          <Card
            key={event.Id}
            sx={{
              mb: 1,
              cursor: 'pointer',
              '&:hover': {
                elevation: 2
              }
            }}
            onClick={() => onEventClick(event)}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: getTaskTypeColor(event.TaskType),
                    mr: 1
                  }}
                />
                <Typography variant="body2" component="div" sx={{ 
                  fontWeight: 500, 
                  fontSize: '0.8rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {event.Subject}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                <strong>ID:</strong> {event.prefixId && event.Id ? `${event.prefixId}-${event.Id}` : event.Id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                {event.TaskType === 'MEETING' 
                  ? `${formatWithNominativeMonth(new Date(event.StartTime), 'EEEE, d MMMM yyyy, HH:mm', { locale: pl })} - ${format(new Date(event.EndTime), 'HH:mm')}`
                  : formatWithNominativeMonth(new Date(event.StartTime), 'EEEE, d MMMM yyyy, HH:mm', { locale: pl })
                }
              </Typography>
              {event.Description && (
                <Typography variant="caption" sx={{ 
                  mb: 0.5, 
                  fontSize: '0.7rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  <strong>Opis:</strong> {event.Description}
                </Typography>
              )}
              {event.assignedToUserId && (
                <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <strong>Przypisane do:</strong>
                  </Typography>
                  <Avatar 
                    sx={{ 
                      width: 16, 
                      height: 16, 
                      bgcolor: 'primary.main',
                      fontSize: '0.5rem'
                    }}
                  >
                    {event.assignedToUserId ? getUserInitials(event.assignedToUserId) : <PersonIcon sx={{ fontSize: '0.5rem' }} />}
                  </Avatar>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {getUserFullNameById(event.assignedToUserId)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={getTaskTypeLabel(event.TaskType)}
                  size="small"
                  sx={{ 
                    backgroundColor: getTaskTypeColor(event.TaskType), 
                    color: 'white',
                    fontSize: '0.6rem',
                    height: '20px',
                    cursor: 'default',
                    pointerEvents: 'none'
                  }}
                />
                <Chip
                  label={getStatusLabel(event.Status)}
                  size="small"
                  sx={{ 
                    backgroundColor: getStatusColor(event.Status), 
                    color: 'white',
                    fontSize: '0.6rem',
                    height: '20px',
                    cursor: 'default',
                    pointerEvents: 'none'
                  }}
                />
                <Chip
                  label={`${getPriorityLabel(event.Priority)}`}
                  size="small"
                  sx={{ 
                    backgroundColor: getPriorityColor(event.Priority), 
                    color: 'white',
                    fontSize: '0.6rem',
                    height: '20px',
                    cursor: 'default',
                    pointerEvents: 'none'
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'month':
        return renderMonthView();
      case 'week':
      case 'workWeek':
        return renderWeekView();
      case 'day':
        return renderDayView();
      case 'agenda':
        return renderAgendaView();
      default:
        return renderMonthView();
    }
  };

  return (
    <Paper ref={calendarRef} elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 2 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Today fontSize="small" />}
              onClick={goToToday}
              sx={{ fontSize: '0.75rem', borderRadius: '9999px'}}
            >
              Dzisiaj
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <IconButton onClick={goToPrevious} size="small">
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'center' }}>
              {getTitle()}
            </Typography>
            <IconButton onClick={goToNext} size="small">
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonGroup variant="outlined" size="small" sx={{ gap: 0 }}>
              <Tooltip title="Dzień">
                <Button
                    onClick={() => handleViewChangeWithCurrentTime('day')}
                    variant={currentView === 'day' ? 'contained' : 'outlined'}
                    sx={{ minWidth: '32px', padding: '4px' }}
                >
                  <ViewDay fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Tydzień">
                <Button
                    onClick={() => handleViewChangeWithCurrentTime('week')}
                    variant={currentView === 'week' ? 'contained' : 'outlined'}
                    sx={{ minWidth: '32px', padding: '4px' }}
                >
                  <ViewWeek fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Tydzień roboczy">
                <Button
                    onClick={() => handleViewChangeWithCurrentTime('workWeek')}
                    variant={currentView === 'workWeek' ? 'contained' : 'outlined'}
                    sx={{ minWidth: '32px', padding: '4px' }}
                >
                  <WorkOutline fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Miesiąc">
                <Button
                    onClick={() => onViewChange('month')}
                    variant={currentView === 'month' ? 'contained' : 'outlined'}
                    sx={{ minWidth: '32px', padding: '4px' }}
                >
                  <CalendarMonth fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Agenda">
                <Button
                    onClick={() => onViewChange('agenda')}
                    variant={currentView === 'agenda' ? 'contained' : 'outlined'}
                    sx={{ minWidth: '32px', padding: '4px' }}
                >
                  <ViewAgenda fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>
      {/* Calendar content */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Ładowanie...</Typography>
          </Box>
        ) : (
          renderCurrentView()
        )}
      </Box>


      {/* Resize Tooltip */}
      {resizeTooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            left: resizeTooltip.x,
            top: resizeTooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {resizeTooltip.time}
        </Box>
      )}

      {/* Drag Tooltip */}
      {dragTooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            left: dragTooltip.x,
            top: dragTooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <div>{dragTooltip.date}</div>
          <div>{dragTooltip.timeRange}</div>
        </Box>
      )}
    </Paper>
  );
};

export default CustomCalendar;
